import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { useAuth } from './AuthContext';
import api from '../utils/api';
import { normalizeProduct } from '../utils/productUtils';

const StoreContext = createContext();
const CART_STORAGE_KEY = 'shoppingCart';

const initialState = {
  cart: [],
  wishlist: [],
  wishlistLoading: false,
  wishlistError: '',
};

const loadStoredCart = () => {
  try {
    const storedCart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY) || '[]');
    if (!Array.isArray(storedCart)) {
      return [];
    }

    return storedCart.filter((item) => (
      item
      && (item.id || item._id)
      && Number.isFinite(item.price)
      && Number.isInteger(item.qty)
      && item.qty > 0
    ));
  } catch (error) {
    console.error('Error restoring cart:', error);
    return [];
  }
};

const createInitialState = () => ({
  ...initialState,
  cart: loadStoredCart(),
});

const mapWishlistResponse = (data) =>
  (data?.wishlist?.items || [])
    .map((item) => normalizeProduct(item.product || item))
    .filter((item) => item.id);

function reducer(state, action) {
  switch (action.type) {
    case 'ADD_TO_CART': {
      const exists = state.cart.find(
        (item) => item.id === action.item.id && item.variant === action.item.variant
      );
      if (exists) {
        return {
          ...state,
          cart: state.cart.map((item) =>
            item.id === action.item.id && item.variant === action.item.variant
              ? { ...item, qty: item.qty + (action.item.qty || 1) }
              : item
          ),
        };
      }

      return {
        ...state,
        cart: [...state.cart, { ...action.item, qty: action.item.qty || 1 }],
      };
    }
    case 'REMOVE_FROM_CART':
      return {
        ...state,
        cart: state.cart.filter(
          (item) => !(item.id === action.id && item.variant === action.variant)
        ),
      };
    case 'UPDATE_QTY':
      return {
        ...state,
        cart: state.cart.map((item) =>
          item.id === action.id && item.variant === action.variant
            ? { ...item, qty: action.qty }
            : item
        ),
      };
    case 'CLEAR_CART':
      return { ...state, cart: [] };
    case 'TOGGLE_WISHLIST_LOCAL': {
      const inWishlist = state.wishlist.some((item) => item.id === action.item.id);
      return {
        ...state,
        wishlist: inWishlist
          ? state.wishlist.filter((item) => item.id !== action.item.id)
          : [...state.wishlist, action.item],
      };
    }
    case 'SET_WISHLIST':
      return { ...state, wishlist: action.items, wishlistError: '' };
    case 'SET_WISHLIST_LOADING':
      return { ...state, wishlistLoading: action.loading };
    case 'SET_WISHLIST_ERROR':
      return { ...state, wishlistError: action.error };
    default:
      return state;
  }
}

export function StoreProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, undefined, createInitialState);
  const { isAuthenticated, loading: authLoading } = useAuth();

  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(state.cart));
    } catch (error) {
      console.error('Error saving cart:', error);
    }
  }, [state.cart]);

  useEffect(() => {
    if (authLoading) {
      return undefined;
    }

    if (!isAuthenticated) {
      dispatch({ type: 'SET_WISHLIST', items: [] });
      dispatch({ type: 'SET_WISHLIST_LOADING', loading: false });
      dispatch({ type: 'SET_WISHLIST_ERROR', error: '' });
      return undefined;
    }

    let ignore = false;

    const loadWishlist = async () => {
      dispatch({ type: 'SET_WISHLIST_LOADING', loading: true });
      dispatch({ type: 'SET_WISHLIST_ERROR', error: '' });

      try {
        const response = await api.get('/wishlist');
        if (!ignore) {
          dispatch({ type: 'SET_WISHLIST', items: mapWishlistResponse(response.data) });
        }
      } catch (error) {
        if (!ignore) {
          dispatch({ type: 'SET_WISHLIST', items: [] });
          dispatch({
            type: 'SET_WISHLIST_ERROR',
            error: error.response?.data?.message || 'Unable to load wishlist right now.',
          });
        }
      } finally {
        if (!ignore) {
          dispatch({ type: 'SET_WISHLIST_LOADING', loading: false });
        }
      }
    };

    loadWishlist();

    return () => {
      ignore = true;
    };
  }, [authLoading, isAuthenticated]);

  const addToCart = (item) => dispatch({ type: 'ADD_TO_CART', item });
  const removeFromCart = (id, variant) => dispatch({ type: 'REMOVE_FROM_CART', id, variant });
  const updateQty = (id, variant, qty) => dispatch({ type: 'UPDATE_QTY', id, variant, qty });
  const clearCart = () => dispatch({ type: 'CLEAR_CART' });

  const refreshWishlist = async () => {
    if (!isAuthenticated) {
      dispatch({ type: 'SET_WISHLIST', items: [] });
      dispatch({ type: 'SET_WISHLIST_ERROR', error: '' });
      return [];
    }

    dispatch({ type: 'SET_WISHLIST_LOADING', loading: true });
    dispatch({ type: 'SET_WISHLIST_ERROR', error: '' });

    try {
      const response = await api.get('/wishlist');
      const items = mapWishlistResponse(response.data);
      dispatch({ type: 'SET_WISHLIST', items });
      return items;
    } catch (error) {
      dispatch({
        type: 'SET_WISHLIST_ERROR',
        error: error.response?.data?.message || 'Unable to load wishlist right now.',
      });
      throw error;
    } finally {
      dispatch({ type: 'SET_WISHLIST_LOADING', loading: false });
    }
  };

  const toggleWishlist = async (item) => {
    const productId = item?.id || item?._id;
    if (!productId) {
      const errorMessage = 'Product id is required to update wishlist.';
      dispatch({ type: 'SET_WISHLIST_ERROR', error: errorMessage });
      return { action: 'error', persisted: false, error: errorMessage };
    }

    const inWishlist = state.wishlist.some((wishlistItem) => wishlistItem.id === productId);

    if (!isAuthenticated) {
      dispatch({ type: 'TOGGLE_WISHLIST_LOCAL', item: normalizeProduct(item) });
      return { action: inWishlist ? 'removed' : 'added', persisted: false };
    }

    dispatch({ type: 'SET_WISHLIST_LOADING', loading: true });
    dispatch({ type: 'SET_WISHLIST_ERROR', error: '' });

    try {
      const response = inWishlist
        ? await api.delete(`/wishlist/item/${productId}`)
        : await api.post('/wishlist/add', { productId });

      dispatch({ type: 'SET_WISHLIST', items: mapWishlistResponse(response.data) });
      return { action: inWishlist ? 'removed' : 'added', persisted: true };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Unable to update wishlist right now.';
      dispatch({
        type: 'SET_WISHLIST_ERROR',
        error: errorMessage,
      });
      return { action: 'error', persisted: true, error: errorMessage };
    } finally {
      dispatch({ type: 'SET_WISHLIST_LOADING', loading: false });
    }
  };

  const isInWishlist = (id) => state.wishlist.some((item) => item.id === id);
  const cartTotal = useMemo(
    () => state.cart.reduce((sum, item) => sum + item.price * item.qty, 0),
    [state.cart]
  );
  const cartCount = useMemo(
    () => state.cart.reduce((sum, item) => sum + item.qty, 0),
    [state.cart]
  );

  return (
    <StoreContext.Provider
      value={{
        ...state,
        addToCart,
        removeFromCart,
        updateQty,
        clearCart,
        toggleWishlist,
        refreshWishlist,
        isInWishlist,
        cartTotal,
        cartCount,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}

export const useStore = () => useContext(StoreContext);
